import {
  BLOCK_MAP,
  getDefaultBlockState,
  getBlockStateOptions
} from "./blockRegistry.ts";
import {
  parseBlockSpec,
  serializeBlockSpec
} from "./blockSpec.ts";

const MAX_PROTOCOL_OPERATIONS = 5000;
const MIN_COORDINATE = -30000000;
const MAX_COORDINATE = 30000000;
const MIN_HEIGHT = -2048;
const MAX_HEIGHT = 2048;

function assertNumber(value, fieldName) {
  if (typeof value !== "number" || !Number.isSafeInteger(value)) {
    throw new Error("Protocol field " + fieldName + " must be an integer.");
  }
  return Number(value);
}

function assertCoordinate(value, fieldName, isHeight) {
  const coordinate = Math.round(assertNumber(value, fieldName));
  const minimum = isHeight ? MIN_HEIGHT : MIN_COORDINATE;
  const maximum = isHeight ? MAX_HEIGHT : MAX_COORDINATE;

  if (coordinate < minimum || coordinate > maximum) {
    throw new Error(
      "Protocol field " + fieldName + " is outside the supported range " +
      minimum + "…" + maximum + "."
    );
  }

  return coordinate;
}

function readPosition(operation, prefix = "") {
  const fieldPrefix = prefix || "";
  return {
    x: assertCoordinate(operation[fieldPrefix + "x"], fieldPrefix + "x", false),
    y: assertCoordinate(operation[fieldPrefix + "y"], fieldPrefix + "y", true),
    z: assertCoordinate(operation[fieldPrefix + "z"], fieldPrefix + "z", false)
  };
}

function validateBlockSpec(
  blockInput,
  fieldName,
  explicitState,
  includeDefaults = true
) {
  if (typeof blockInput !== "string" || blockInput.length === 0) {
    throw new Error("Protocol field " + fieldName + " requires a block id.");
  }

  if (blockInput === "air" || blockInput === "minecraft:air") {
    return null;
  }

  const parsed = parseBlockSpec(blockInput, explicitState);
  if (!parsed || !BLOCK_MAP.has(parsed.blockId)) {
    throw new Error(
      "Unknown block id: " +
      (parsed ? parsed.blockId : blockInput) +
      "."
    );
  }

  const options = getBlockStateOptions(parsed.blockId);
  for (const [key, value] of Object.entries(parsed.state)) {
    const allowed = options[key];
    if (!allowed || !allowed.includes(value)) throw new Error("Invalid state " + key + "=" + value + " for " + parsed.blockId + ".");
  }
  return serializeBlockSpec({
    blockId: parsed.blockId,
    state:
      includeDefaults === false
        ? { ...parsed.state }
        : {
            ...getDefaultBlockState(parsed.blockId),
            ...parsed.state
          }
  });
}

function requireBlock(operation) {
  return validateBlockSpec(
    operation.block,
    "block",
    operation.state || {}
  );
}

// Compose affine transforms so nested offsets rotate with their parent.
function transformPosition(position, transform) {
  return {
    x: assertCoordinate(transform.xx * position.x + transform.xz * position.z + transform.offsetX, "transformed x", false),
    y: assertCoordinate(position.y + transform.offsetY, "transformed y", true),
    z: assertCoordinate(transform.zx * position.x + transform.zz * position.z + transform.offsetZ, "transformed z", false)
  };
}

function mergeTransform(parent, operation) {
  const rotation = operation.rotateY ?? 0;
  if (![0, 90, 180, 270].includes(rotation)) throw new Error("rotateY supports only 0, 90, 180, or 270 degrees.");
  for (const field of ["mirrorX", "mirrorZ"]) {
    if (operation[field] !== undefined && typeof operation[field] !== "boolean") throw new Error(field + " must be boolean.");
  }
  const signX = operation.mirrorX ? -1 : 1;
  const signZ = operation.mirrorZ ? -1 : 1;
  const [cos, sin] = rotation === 0 ? [1, 0] : rotation === 90 ? [0, 1] : rotation === 180 ? [-1, 0] : [0, -1];
  const child = { xx: cos * signX, xz: -sin * signZ, zx: sin * signX, zz: cos * signZ };
  const offset = transformPosition({
    x: operation.offsetX === undefined ? 0 : assertCoordinate(operation.offsetX, "offsetX", false),
    y: operation.offsetY === undefined ? 0 : assertCoordinate(operation.offsetY, "offsetY", true),
    z: operation.offsetZ === undefined ? 0 : assertCoordinate(operation.offsetZ, "offsetZ", false)
  }, parent);
  return {
    xx: parent.xx * child.xx + parent.xz * child.zx,
    xz: parent.xx * child.xz + parent.xz * child.zz,
    zx: parent.zx * child.xx + parent.zz * child.zx,
    zz: parent.zx * child.xz + parent.zz * child.zz,
    offsetX: offset.x, offsetY: offset.y, offsetZ: offset.z
  };
}

function transformBlockSpec(blockSpec, transform) {
  const parsed = parseBlockSpec(blockSpec);
  if (!parsed) return null;
  const state = { ...parsed.state };
  const vectors = { north: [0, -1], east: [1, 0], south: [0, 1], west: [-1, 0] };
  function direction(name) {
    const vector = vectors[name];
    if (!vector) return name;
    const x = transform.xx * vector[0] + transform.xz * vector[1];
    const z = transform.zx * vector[0] + transform.zz * vector[1];
    return Object.keys(vectors).find(key => vectors[key][0] === x && vectors[key][1] === z);
  }
  if (state.facing) state.facing = direction(state.facing);
  if (state.axis === "x") state.axis = transform.xx === 0 ? "z" : "x";
  else if (state.axis === "z") state.axis = transform.zz === 0 ? "x" : "z";
  for (const key of Object.keys(vectors)) delete state[key];
  for (const key of Object.keys(vectors)) if (parsed.state[key] !== undefined) state[direction(key)] = parsed.state[key];
  if (state.rotation !== undefined) {
    const radians = Number(state.rotation) * Math.PI / 8;
    const x = -Math.sin(radians), z = Math.cos(radians);
    const transformedX = transform.xx * x + transform.xz * z;
    const transformedZ = transform.zx * x + transform.zz * z;
    state.rotation = String((Math.round(Math.atan2(-transformedX, transformedZ) * 8 / Math.PI) + 16) % 16);
  }
  if (transform.xx * transform.zz - transform.xz * transform.zx < 0) {
    if (state.hinge === "left") state.hinge = "right";
    else if (state.hinge === "right") state.hinge = "left";
    if (state.shape?.endsWith("_left")) state.shape = state.shape.replace("_left", "_right");
    else if (state.shape?.endsWith("_right")) state.shape = state.shape.replace("_right", "_left");
  }
  return serializeBlockSpec({ blockId: parsed.blockId, state });
}

function consumeWork(context, count) {
  context.work += count;
  if (!Number.isSafeInteger(context.work) || context.work > 1000000) throw new Error("Protocol exceeds the 1,000,000 voxel-work budget; split the build into smaller batches.");
}

function executeOperation(world, operation, transform, context, depth = 0) {
  if (++context.operations > MAX_PROTOCOL_OPERATIONS || depth > 32) throw new Error("Protocol exceeds the nested operation/depth limit.");
  if (!operation || typeof operation !== "object") {
    throw new Error("Protocol operations must be objects.");
  }

  if (operation.op === "group") {
    if (!Array.isArray(operation.ops)) {
      throw new Error("A group operation requires an ops array.");
    }

    const childTransform = mergeTransform(transform, operation);
    let groupChanged = 0;
    operation.ops.forEach(function (childOperation) {
      groupChanged += executeOperation(world, childOperation, childTransform, context, depth + 1);
    });
    return groupChanged;
  }

  if (operation.op === "set") {
    consumeWork(context, 1);
    const point = transformPosition(readPosition(operation), transform);
    let changed = world.setBlock(
      point.x,
      point.y,
      point.z,
      transformBlockSpec(requireBlock(operation), transform)
    ) ? 1 : 0;

    if (operation.blockEntity) {
      changed += world.setBlockEntityData(
        point.x,
        point.y,
        point.z,
        operation.blockEntity
      ) ? 1 : 0;
    }

    return changed;
  }

  if (operation.op === "blockEntity") {
    consumeWork(context, 1);
    const point = transformPosition(
      readPosition(operation),
      transform
    );

    if (!world.hasBlock(point.x, point.y, point.z)) {
      throw new Error(
        "blockEntity operation targets an empty position at " +
          point.x + "," + point.y + "," + point.z + "."
      );
    }

    if (!operation.data || typeof operation.data !== "object") {
      throw new Error(
        "blockEntity operation requires a data object."
      );
    }

    return world.setBlockEntityData(
      point.x,
      point.y,
      point.z,
      operation.data
    ) ? 1 : 0;
  }

  if (operation.op === "box" || operation.op === "hollowBox" || operation.op === "line") {
    const first = transformPosition(readPosition(operation, ""), transform);
    const second = transformPosition({
      x: assertCoordinate(operation.x2, "x2", false),
      y: assertCoordinate(operation.y2, "y2", true),
      z: assertCoordinate(operation.z2, "z2", false)
    }, transform);
    const blockSpec = transformBlockSpec(
      requireBlock(operation),
      transform
    );

    consumeWork(context, operation.op === "line"
      ? Math.max(Math.abs(first.x - second.x), Math.abs(first.y - second.y), Math.abs(first.z - second.z)) + 1
      : (Math.abs(first.x - second.x) + 1) * (Math.abs(first.y - second.y) + 1) * (Math.abs(first.z - second.z) + 1));
    if (operation.op === "hollowBox" && operation.thickness !== undefined && (typeof operation.thickness !== "number" || !Number.isSafeInteger(operation.thickness) || operation.thickness < 1)) throw new Error("thickness must be a positive integer.");
    if (operation.op === "box") {
      return world.fill(first.x, first.y, first.z, second.x, second.y, second.z, blockSpec);
    }

    if (operation.op === "hollowBox") {
      return world.hollowBox(
        first.x,
        first.y,
        first.z,
        second.x,
        second.y,
        second.z,
        blockSpec,
        Number(operation.thickness || 1)
      );
    }

    return world.line(first.x, first.y, first.z, second.x, second.y, second.z, blockSpec);
  }

  if (operation.op === "cylinder") {
    const center = transformPosition(readPosition(operation), transform);
    const radius = assertCoordinate(operation.radius, "radius", false);
    const height = assertCoordinate(operation.height, "height", true);
    if (radius < 0 || height < 1) throw new Error("Cylinder radius must be nonnegative and height positive.");
    if (operation.hollow !== undefined && typeof operation.hollow !== "boolean") throw new Error("hollow must be boolean.");
    transformPosition({ x: center.x + radius, y: center.y + height - 1, z: center.z + radius }, {xx:1,xz:0,zx:0,zz:1,offsetX:0,offsetY:0,offsetZ:0});
    assertCoordinate(center.x - radius, "cylinder min x", false);
    assertCoordinate(center.z - radius, "cylinder min z", false);
    consumeWork(context, (2 * radius + 1) ** 2 * height);
    return world.cylinder(
      center.x,
      center.y,
      center.z,
      assertCoordinate(operation.radius, "radius", false),
      assertCoordinate(operation.height, "height", true),
      transformBlockSpec(requireBlock(operation), transform),
      Boolean(operation.hollow)
    );
  }

  if (operation.op === "sphere") {
    const center = transformPosition(readPosition(operation), transform);
    const radius = assertCoordinate(operation.radius, "radius", false);
    if (radius < 1) throw new Error("Sphere radius must be positive.");
    if (operation.hollow !== undefined && typeof operation.hollow !== "boolean") throw new Error("hollow must be boolean.");
    for (const axis of ["x", "y", "z"]) {
      assertCoordinate(center[axis] - radius, "sphere min " + axis, axis === "y");
      assertCoordinate(center[axis] + radius, "sphere max " + axis, axis === "y");
    }
    consumeWork(context, (2 * radius + 1) ** 3);
    return world.sphere(
      center.x,
      center.y,
      center.z,
      assertCoordinate(operation.radius, "radius", false),
      transformBlockSpec(requireBlock(operation), transform),
      Boolean(operation.hollow)
    );
  }

  if (operation.op === "replace") {
    consumeWork(context, world.blocks.size);
    const fromBlockSpec = validateBlockSpec(
      operation.from,
      "from",
      operation.fromState || {},
      false
    );
    const toBlockSpec = validateBlockSpec(
      operation.to,
      "to",
      operation.toState || {}
    );
    return world.replace(fromBlockSpec, toBlockSpec);
  }

  throw new Error("Unsupported protocol operation: " + String(operation.op));
}

export function executeBuildProtocol(world, protocolInput) {
  const protocol = typeof protocolInput === "string"
    ? JSON.parse(protocolInput)
    : protocolInput;

  if (!protocol || protocol.format !== "minecraft-design-protocol" || protocol.version !== 1 || !Array.isArray(protocol.ops)) {
    throw new Error("MDP v1 requires an ops array.");
  }

  if (protocol.ops.length > MAX_PROTOCOL_OPERATIONS) {
    throw new Error("Protocol exceeds the operation limit of " + MAX_PROTOCOL_OPERATIONS + ".");
  }

  if (world.activeTransaction) throw new Error("Cannot run a protocol inside an active edit transaction.");
  const rootTransform = {xx: 1, xz: 0, zx: 0, zz: 1, offsetX: 0, offsetY: 0, offsetZ: 0};
  const context = {operations: 0, work: 0};

  world.beginTransaction(protocol.name || "Run MDP protocol");
  let changed = 0;

  try {
    world.runBatch(function () {
      protocol.ops.forEach(function (operation) {
        changed += executeOperation(world, operation, rootTransform, context);
      });
    });
    world.commitTransaction();
  } catch (error) {
    world.cancelTransaction();
    throw error;
  }

  return changed;
}

export function createProtocolExample() {
  return {
    format: "minecraft-design-protocol",
    version: 1,
    name: "Stone pavilion example",
    ops: [
      {
        op: "box",
        x: -5,
        y: 0,
        z: -5,
        x2: 5,
        y2: 0,
        z2: 5,
        block: "polished_andesite"
      },
      {
        op: "group",
        offsetY: 1,
        ops: [
          {
            op: "hollowBox",
            x: -4,
            y: 0,
            z: -4,
            x2: 4,
            y2: 5,
            z2: 4,
            block: "stone_bricks"
          },
          {
            op: "box",
            x: -1,
            y: 0,
            z: -4,
            x2: 1,
            y2: 3,
            z2: -4,
            block: "air"
          }
        ]
      },
      {
        op: "cylinder",
        x: 0,
        y: 6,
        z: 0,
        radius: 6,
        height: 2,
        block: "deepslate_tile_slab",
        state: { type: "top", waterlogged: "false" },
        hollow: false
      },
      {
        op: "set",
        x: 0,
        y: 4,
        z: -4,
        block: "lantern",
        state: { hanging: "true", waterlogged: "false" }
      },
      {
        op: "set",
        x: 4,
        y: 1,
        z: 0,
        block: "stone_brick_stairs",
        state: {
          facing: "west",
          half: "bottom",
          shape: "straight",
          waterlogged: "false"
        }
      }
    ]
  };
}
